import { Card, Result } from 'antd'
import React, { FC } from 'react'
import { Link, Route, Switch, RouteProps, Redirect } from 'react-router-dom'
import { Authenticating } from './components'
import Dashboard from './Dashboard'
import { ServiceProvider, EndCustomers, EndCustomer, Resellers, Reseller, Users, ServiceProviders, Admin, Invites, ClaimInvite, Terms, LicenseUsageReport } from './pages'
import ApiTokens from './pages/ApiTokens'
import { AuthProvider, useAuth } from './providers/AuthProvider'

interface PrivateRouteProps extends RouteProps {
	requiresPermissions?: string[]
}

const NotFound: FC = () => (
	<Card bordered={false}>
		<Result status='404' title='Page not found' subTitle='No page was found at this URL' extra={<Link to='/endcustomer'>Back Home</Link>} />
	</Card>
)

const PrivateRoute: FC<PrivateRouteProps> = ({ children, requiresPermissions, ...rest }) => {
	const { authenticating, userHasPermission } = useAuth()

	if (authenticating) {
		return <Authenticating visible={true} />
	}

	if (requiresPermissions && !userHasPermission(requiresPermissions)) {
		return <NotFound />
	}

	return (
		<Dashboard theme='dark'>
			<Route {...rest}>{children}</Route>
		</Dashboard>
	)
}

const AuthenticatedRoute: FC<PrivateRouteProps> = ({ children, ...rest }) => {
	return (
		<AuthProvider>
			<PrivateRoute {...rest}>{children}</PrivateRoute>
		</AuthProvider>
	)
}

const AuthenticatedApp: FC = () => {
	return (
		<Switch>
			<Redirect exact from='/' to='/serviceprovider' />
			<AuthenticatedRoute exact path='/serviceprovider'>
				<ServiceProviders />
			</AuthenticatedRoute>
			<AuthenticatedRoute exact path='/license-usage-report'>
				<LicenseUsageReport />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/serviceprovider/:id/:tab'>
				<ServiceProvider />
			</AuthenticatedRoute>
			<AuthenticatedRoute exact path='/reseller'>
				<Resellers />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/reseller/:id/:tab'>
				<Reseller />
			</AuthenticatedRoute>
			<AuthenticatedRoute exact path='/endcustomer'>
				<EndCustomers />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/endcustomer/:id/:tab'>
				<EndCustomer />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/users' requiresPermissions={['User.All.ReadWrite']}>
				<Users />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/admin' requiresPermissions={['ServiceProvider.All.ReadWrite']}>
				<Admin />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/invites' requiresPermissions={['User.All.ReadWrite']}>
				<Invites />
			</AuthenticatedRoute>
			<AuthenticatedRoute path='/api-tokens' requiresPermissions={['ExternalAPI.All.Access']}>
				<ApiTokens />
			</AuthenticatedRoute>
			<Route path='/claim_invite'>
				<ClaimInvite />
			</Route>
			<Route path='/terms'>
				<Terms />
			</Route>
			<Route>
				<NotFound />
			</Route>
		</Switch>
	)
}

export default AuthenticatedApp
